import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { unifiedPushService } from '@/services/UnifiedPushNotificationService'
import { useAuth } from '@/features/auth/hooks/useAuth'

// Simple SVG icons
const BellIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
)

const XIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
)

const AlertIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.866-.833-2.635 0L4.168 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
)

export function PushNotificationPermissionBanner() {
  const [permission, setPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt')
  const [isVisible, setIsVisible] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)
  const { user } = useAuth()

  useEffect(() => {
    if (!user) return

    // Check current permission status
    const checkPermission = async () => {
      const status = await unifiedPushService.getPermissionStatus()
      setPermission(status)
      
      // Show banner if permission is not granted and not denied
      setIsVisible(status === 'prompt')
    }

    checkPermission()
  }, [user])

  const handleEnableNotifications = async () => {
    setIsRequesting(true)
    
    try {
      const granted = await unifiedPushService.requestPermissions()
      
      if (granted) {
        setPermission('granted')
        setIsVisible(false)
        
        // Show success message
        const event = new CustomEvent('push-notification-received', {
          detail: {
            title: 'Notifications Enabled!',
            body: 'You will now receive challenge reminders and updates.',
            platform: 'web'
          }
        })
        window.dispatchEvent(event)
      } else {
        setPermission('denied')
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error)
    } finally {
      setIsRequesting(false)
    }
  }

  const handleDismiss = () => {
    setIsVisible(false)
    // Store dismissal in localStorage to not show again for 7 days
    localStorage.setItem('push_notification_banner_dismissed', Date.now().toString())
  }

  // Check if banner was recently dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('push_notification_banner_dismissed')
    if (dismissed) {
      const dismissedTime = parseInt(dismissed)
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000
      
      if (Date.now() - dismissedTime < sevenDaysInMs) {
        setIsVisible(false)
      }
    }
  }, [])

  if (!user) return null

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4"
        >
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg shadow-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <BellIcon />
              </div>
              
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">
                  Enable Push Notifications
                </h3>
                <p className="text-sm text-white/90 mb-3">
                  Get reminders for your daily challenges, even when the app is closed
                </p>
                
                {permission === 'denied' ? (
                  <div className="flex items-center gap-2 text-sm bg-white/20 rounded-md p-2">
                    <AlertIcon />
                    <span>
                      Notifications blocked. Please enable them in your browser settings.
                    </span>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleEnableNotifications}
                      disabled={isRequesting}
                      className="flex items-center gap-2 bg-white text-primary-600 px-4 py-2 rounded-md font-medium text-sm hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRequesting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                          <span>Enabling...</span>
                        </>
                      ) : (
                        <>
                          <CheckIcon />
                          <span>Enable Now</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={handleDismiss}
                      className="text-white/80 hover:text-white p-2 rounded-md hover:bg-white/10 transition-colors"
                      aria-label="Dismiss"
                    >
                      <XIcon />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}